/**
 * TOPページ
 * 各種スライダー、マーキー、検索バー、閲覧履歴などの制御
 */

import Splide from "@splidejs/splide";
import type { Options } from "@splidejs/splide";
import { AutoScroll } from "@splidejs/splide-extension-auto-scroll";
import { breakpoint } from "../constant/breakpoint";
import { initProductSlider, initProgressSlider } from "../util/sliderHelpers";

/**
 * ======================================
 * Search Bar - 検索バープレースホルダー自動切り替え
 * ======================================
 */

/**
 * プレースホルダーの自動切り替えを初期化
 */
const initSearchBarPlaceholder = (): void => {
  const searchInputs = document.querySelectorAll<HTMLInputElement>(
    ".p-search-bar__input[data-placeholder-words]",
  );

  if (searchInputs.length === 0) return;

  for (const input of Array.from(searchInputs)) {
    const wordsStr = input.getAttribute("data-placeholder-words");
    const interval = Number.parseFloat(
      input.getAttribute("data-placeholder-interval") || "5",
    );

    if (!wordsStr || wordsStr.trim() === "") {
      return;
    }

    // 改行区切りでワードを分割し、空の要素を除外
    const words = wordsStr
      .split("\n")
      .map((word: string) => word.trim())
      .filter((word: string) => word !== "");

    if (words.length === 0) {
      return;
    }

    let currentIndex = 0;
    let intervalId: number | null = null;
    let isPaused = false;

    const updatePlaceholder = (): void => {
      if (isPaused || words.length === 0) {
        return;
      }

      // フェードアウト
      input.classList.add("placeholder-fade-out");

      // フェードアウト完了後にテキストを変更してフェードイン
      setTimeout(() => {
        // プレースホルダーを変更
        currentIndex = (currentIndex + 1) % words.length;
        input.placeholder = words[currentIndex];

        // すぐにフェードインを開始
        requestAnimationFrame(() => {
          input.classList.remove("placeholder-fade-out");
        });
      }, 400); // フェードアウトの時間（transition時間と合わせる）
    };

    // 初期化
    if (words.length > 0) {
      input.placeholder = words[0];
      currentIndex = 0;
    }

    // インターバルを開始
    if (words.length > 1) {
      intervalId = window.setInterval(updatePlaceholder, interval * 1000);
    }

    // フォーカス時に停止
    input.addEventListener("focus", () => {
      isPaused = true;
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    });

    // フォーカス解除時に再開
    input.addEventListener("blur", () => {
      isPaused = false;
      if (words.length > 1 && !intervalId) {
        // 現在のプレースホルダーを再設定
        if (words.length > 0) {
          input.placeholder = words[currentIndex];
        }
        intervalId = window.setInterval(updatePlaceholder, interval * 1000);
      }
    });
  }
};

/**
 * ======================================
 * Category List - カテゴリーリスト VIEW MORE / VIEW LESS
 * ======================================
 */

/**
 * カテゴリーリストの VIEW MORE / VIEW LESS を初期化
 */
const initCategoryListViewMore = (): void => {
  const section = document.querySelector<HTMLElement>("[data-category-list]");
  const btn = document.querySelector<HTMLButtonElement>(
    "[data-category-list-btn]",
  );
  const inner = section?.querySelector<HTMLElement>(
    ".p-top-category-list__inner",
  );

  if (!section || !btn || !inner) return;

  btn.addEventListener("click", () => {
    const isExpanded = section.classList.contains("is-expanded");

    if (isExpanded) {
      // 閉じる: scrollHeight から CSS の max-height へトランジション
      section.classList.remove("is-expanded");
      inner.style.maxHeight = "";
    } else {
      // 開く: CSS の max-height から scrollHeight へトランジション
      section.classList.add("is-expanded");
      inner.style.maxHeight = `${inner.scrollHeight}px`;
    }

    btn.setAttribute("aria-expanded", String(!isExpanded));
    const span = btn.querySelector("span");
    if (span) span.textContent = isExpanded ? "VIEW MORE" : "VIEW LESS";
  });
};

/**
 * ======================================
 * Collection Marquee - コレクションマーキー
 * ======================================
 */

/**
 * 秒設定を px/frame 速度に変換（秒が小さいほど速い）
 * 10s → 0.5 px/frame（速い）
 * 100s → 0.05 px/frame（遅い）
 */
const secondsToSpeed = (seconds: number): number => {
  return 5 / seconds;
};

/**
 * コレクションマーキーを初期化
 */
const initCollectionMarquee = (): void => {
  const container = document.querySelector<HTMLElement>(
    "[data-collection-marquee]",
  );
  if (!container) return;

  const splideEls = container.querySelectorAll<HTMLElement>(
    "[data-marquee-splide]",
  );
  if (splideEls.length === 0) return;

  // PC/SP の速度設定を取得
  const secondsPc = Number.parseInt(container.dataset.speedPc || "30", 10);
  const secondsSp = Number.parseInt(container.dataset.speedSp || "20", 10);
  const speedPc = secondsToSpeed(secondsPc);
  const speedSp = secondsToSpeed(secondsSp);

  const splideInstances: Splide[] = [];
  const mediaQuery = window.matchMedia(
    `(max-width: ${Number.parseInt(breakpoint.md) - 1}px)`,
  );

  // 現在のビューポートに応じた速度を取得
  const getCurrentSpeed = (): number =>
    mediaQuery.matches ? speedSp : speedPc;

  for (const splideEl of Array.from(splideEls)) {
    const isReverse = splideEl.dataset.reverse === "true";
    const initialSpeed = getCurrentSpeed();
    // SPのときは2列とも右から左に流れる（逆方向にしない）
    const shouldReverse = mediaQuery.matches ? false : isReverse;

    const options: Options = {
      type: "loop",
      drag: "free",
      focus: "center",
      perPage: 6,
      autoWidth: true,
      gap: "24px",
      pagination: false,
      arrows: false,
      clones: 10, // 修正：クローンを十分に生成して見切れを防ぐ
      cloneStatus: true, // 修正：クローンステータスを有効化
      autoScroll: {
        speed: shouldReverse ? -initialSpeed : initialSpeed,
        pauseOnHover: true,
        pauseOnFocus: true,
      },
      breakpoints: {
        [Number.parseInt(breakpoint.md) - 1]: {
          perPage: 3,
          clones: 10, // 修正：SPでもクローンを十分に生成
        },
      },
    };

    try {
      const splide = new Splide(splideEl, options).mount({ AutoScroll });
      splideInstances.push(splide);
    } catch (error) {
      console.error("Failed to initialize Collection Marquee:", error);
    }
  }

  // ビューポート変更時に速度を更新
  mediaQuery.addEventListener("change", () => {
    const newSpeed = getCurrentSpeed();
    for (const [index, splide] of splideInstances.entries()) {
      const isReverse = splideEls[index].dataset.reverse === "true";
      // SPのときは2列とも右から左に流れる（逆方向にしない）
      const shouldReverse = mediaQuery.matches ? false : isReverse;
      const autoScroll = splide.Components.AutoScroll;
      if (autoScroll) {
        splide.options = {
          autoScroll: {
            speed: shouldReverse ? -newSpeed : newSpeed,
          },
        };
      }
    }
  });
};

/**
 * ======================================
 * Featured Pages - おすすめ特集スライダー
 * ======================================
 */

/**
 * おすすめ特集スライダーを初期化（同一ページに複数セクションがあっても各インスタンスをマウント）
 */
const initFeaturedPages = (): void => {
  const sliderElements = document.querySelectorAll<HTMLElement>(
    "[data-featured-pages-slider]",
  );

  if (sliderElements.length === 0) return;

  const spBreakpoint = Number.parseInt(breakpoint.md) - 1;

  for (const sliderElement of sliderElements) {
    // SPのみ2行グリッド時は perPage 2 ＋ 余白は SCSS の grid gap。PCは常に 4 列 1 段（要素ごとに data 属性を参照）
    const isTwoRowLayout =
      sliderElement.dataset.featuredPagesTwoRows === "true";
    const spBreakpointOptions = isTwoRowLayout
      ? {
          perPage: 2,
          perMove: 2,
          gap: "0px",
          padding: { left: "16px", right: "16px" },
          arrows: false,
          fixedWidth: "40vw",
        }
      : {
          perPage: 1,
          perMove: 1,
          // 修正: SP1行表示はベースの gap 24px が残るため 8px を明示
          gap: "8px",
          padding: { left: "16px", right: "16px" },
          arrows: false,
          fixedWidth: "44vw",
        };

    const splide = new Splide(sliderElement, {
      perPage: 4,
      perMove: 1,
      gap: "24px",
      padding: { left: 0, right: 0 },
      arrows: true,
      pagination: false,
      breakpoints: {
        [spBreakpoint]: spBreakpointOptions,
      },
    });

    splide.mount();
  }
};

/**
 * ======================================
 * Instagram Products - Instagram紹介アイテムスライダー
 * ======================================
 */

const INSTAGRAM_SLIDER_OPTIONS = {
  perPage: 4,
  perMove: 1,
  gap: "24px",
  padding: 0,
  arrows: true,
  pagination: false,
  breakpoints: {
    [Number.parseInt(breakpoint.md) - 1]: {
      perPage: 1,
      gap: "8px",
      padding: { left: "16px", right: "16px" },
      arrows: false,
      fixedWidth: "64%",
    },
  },
} as const;

/**
 * Instagram紹介アイテムスライダーを初期化
 */
const initInstagramProducts = (): void => {
  const sliderElements = document.querySelectorAll<HTMLElement>(
    "[data-instagram-products-slider]",
  );

  for (const sliderElement of Array.from(sliderElements)) {
    const splide = new Splide(sliderElement, { ...INSTAGRAM_SLIDER_OPTIONS });
    splide.mount();
  }
};

/**
 * ======================================
 * MV Slider - メインビジュアルスライダー
 * ======================================
 */

/**
 * MVスライダーを初期化（アクティブスライド制御付き）
 */
const initMvSlider = (): void => {
  const sliders = document.querySelectorAll<HTMLElement>(
    "[data-progress-slider='mv']",
  );

  if (sliders.length === 0) return;

  // 共通のプログレスバー機能を初期化してSplideインスタンスを取得
  const splideInstances = initProgressSlider(
    "[data-progress-slider='mv']",
    "[data-progress-slider-pagination]",
    {
      type: "loop",
      perPage: 1,
      perMove: 1,
      gap: "3%",
      pagination: true,
      arrows: true,
      focus: "center",
      autoplay: false,
      speed: 800,
      rewindSpeed: 800,
      pauseOnHover: true,
      pauseOnFocus: true,
      autoWidth: true,
      breakpoints: {
        [Number.parseInt(breakpoint.md) - 1]: {
          gap: "7%",
          padding: { left: "16px", right: "16px" },
        },
      },
    },
  );

  // MVスライダー固有のアクティブスライド制御
  splideInstances.forEach((splide, index) => {
    const slider = sliders[index];
    if (!slider) return;

    // スライドのアクティブ状態を管理
    const updateActiveSlides = (activeIndex: number) => {
      const slides = slider.querySelectorAll(
        ".splide__slide",
      ) as NodeListOf<HTMLElement>;
      for (const slide of Array.from(slides)) {
        slide.classList.remove("is-active");
      }
      try {
        const activeSlide = splide.Components.Slides.getAt(activeIndex);
        if (activeSlide?.slide) {
          activeSlide.slide.classList.add("is-active");
        }
      } catch (e) {
        const slideArray = Array.from(slides);
        const targetSlide = slideArray.find(
          (slide: HTMLElement, index: number) => {
            const dataIndex = slide.getAttribute("data-splide-index");
            return dataIndex
              ? Number.parseInt(dataIndex, 10) === activeIndex
              : index === activeIndex;
          },
        );
        if (targetSlide) {
          targetSlide.classList.add("is-active");
        } else if (slideArray[activeIndex]) {
          slideArray[activeIndex].classList.add("is-active");
        }
      }
    };

    // 初期状態でアクティブスライドを設定
    updateActiveSlides(splide.index);

    // スライド移動時にアクティブスライドを更新
    splide.on("move", (newIndex) => {
      updateActiveSlides(newIndex);
    });

    splide.on("moved", (newIndex) => {
      updateActiveSlides(newIndex);
    });
  });
};

/**
 * ======================================
 * Popular Products - 人気商品スライダー
 * ======================================
 */

/**
 * 人気商品スライダーを初期化
 */
const initPopularProducts = (): void => {
  initProductSlider("[data-product-slider='popular']");
};

/**
 * ======================================
 * Restocked Products - 再入荷商品スライダー
 * ======================================
 */

/**
 * 再入荷商品スライダーを初期化
 */
const initRestockedProducts = (): void => {
  initProductSlider("[data-product-slider='restocked']");
};

/**
 * ======================================
 * Sale Products - セール商品スライダー
 * ======================================
 */

/**
 * セール商品スライダーを初期化
 */
const initSaleProducts = (): void => {
  initProductSlider("[data-product-slider='sale']");
};

/**
 * ======================================
 * メインエクスポート関数
 * ======================================
 */

/**
 * TOPページの全機能を初期化
 */
export const top = (): void => {
  // MVスライダーを初期化
  initMvSlider();
  // コレクションマーキーを初期化
  initCollectionMarquee();
  // おすすめ特集スライダーを初期化
  initFeaturedPages();
  // Instagram紹介アイテムスライダーを初期化
  initInstagramProducts();
  // 人気商品スライダーを初期化
  initPopularProducts();
  // セール商品スライダーを初期化
  initSaleProducts();
  // 再入荷商品スライダーを初期化
  initRestockedProducts();
  // カテゴリーリスト VIEW MORE / VIEW LESS を初期化
  initCategoryListViewMore();
  // 検索バープレースホルダー自動切り替えを初期化
  initSearchBarPlaceholder();
};
